import React from 'react'
import PropTypes from 'prop-types'
import { GridWrapper, GridItem } from '@sainsburys-tech/luna-grid'
import { Card } from '@sainsburys-tech/luna-card'
import {
  Display1,
  Display2,
  Body1,
  Display6
} from '@sainsburys-tech/luna-typography'
import { OutlinedButton } from '@sainsburys-tech/luna-button'
import SearchCalendarStatus from './SearchCalendarStatus'
import useWeeklySearchStatus from '../../customHooks/useWeeklySearchStatus'

function SearchCard({
  title,
  buttonText,
  onClick,
  displayNames,
  counts,
  gridDefaultSize,
  displaySecondaryButton,
  secondaryButtonText,
  onSecondaryClick,
}) {
  const titleForIds = title.toLowerCase()
  const buttonTextForIds = buttonText.replace(' ', '-').toLowerCase()

  const {
    loading, error, sainsburysStatus, argosStatus
  } = useWeeklySearchStatus()

  return (
    <GridWrapper className="ln-u-flush-left">
      <Card className="ln-u-margin-top*2">
        <GridItem className="ln-u-hard-left" size={{ default: '1/1' }}>
          <Display2
            id={`${titleForIds}-title`}
            data-testid={`${titleForIds}-title`}
          >
            {title}
          </Display2>
        </GridItem>
        { title === 'People'
        && (
        <>
          <GridItem className="ln-u-hard-left ln-u-margin-top*2" size={{ default: '1/1' }}>
            <Display1 className="ln-u-hard-left" size={{ default: '1/1' }}>
              Search progress
            </Display1>
          </GridItem>
          <GridItem className="ln-u-hard-left" size={{ default: '1/1' }}>
            <Body1>
              Searches must be conducted daily and a minimum of 25% of your colleagues should be searched every week.
            </Body1>
          </GridItem>
          {Object.keys(counts).includes('sainsburys') && (
            <GridItem className="ln-u-hard-left" size={{ default: '1/1' }}>
              <SearchCalendarStatus
                loading={loading}
                error={!!error}
                weeklyStatus={sainsburysStatus}
                title="Last Search Sainsburys"
              />
            </GridItem>
          )}

          {Object.keys(counts).includes('sainsburys') && Object.keys(counts).includes('argos') && (
            <GridItem className="ln-u-hard-left" size={{ default: '1/1' }}>
              <div style={{ marginTop: '30px' }} />
            </GridItem>
          )}

          {Object.keys(counts).includes('argos') && (
            <GridItem className="ln-u-hard-left" size={{ default: '1/1' }}>
              <SearchCalendarStatus
                loading={loading}
                error={!!error}
                weeklyStatus={argosStatus}
                title="Last Search Argos"
              />
            </GridItem>
          )}
          <GridItem className="ln-u-hard-left ln-u-margin-top*2" size={{ default: '1/1' }}>
            <Display1 className="ln-u-hard-left" size={{ default: '1/1' }}>
              Searches needed
            </Display1>
          </GridItem>
          {Object.keys(counts).includes('argos') && Object.keys(counts).includes('sainsburys') && (
            <GridItem className="ln-u-hard-left" size={{ default: '1/1' }}>
              <Body1>
                {counts.argos + counts.sainsburys}
                {' in total'}
              </Body1>
            </GridItem>
          )}
        </>
        )}
        {Object.values(counts).map((value, index) => (
          <GridItem
            className="ln-u-hard-left u-text-center"
            size={{ default: gridDefaultSize }}
          >
            <Display6
              id={`${buttonTextForIds}-${index}-count`}
              data-testid={`${buttonTextForIds}-${index}-count`}
            >
              {value}
            </Display6>
          </GridItem>
        ))}
        {Object.keys(counts).map((key) => (
          <GridItem
            className="ln-u-hard-left u-text-center"
            size={{ default: gridDefaultSize }}
          >
            <Body1
              id={`${titleForIds}-${key}-count-label`}
              data-testid={`${titleForIds}-${key}-count-label`}
            >
              {/* gets the first element in an array of property values for displayNames where the object
                key for displayNames passed in as props match against the property key in the counts object
                that is being looped through */}
              {
                Object.values(
                  Object.fromEntries(
                    Object.entries(displayNames).filter(
                      ([propKey, _]) => propKey === key
                    )
                  )
                )[0]
              }
            </Body1>
          </GridItem>
        ))}
        <GridItem className="ln-u-hard-left" size={{ default: '1/1' }}>
          <OutlinedButton
            id={`${buttonTextForIds}-secondary-btn`}
            className="ln-u-margin-top*2"
            fullWidth
            onClick={onClick}
          >
            {buttonText}
          </OutlinedButton>
        </GridItem>
        {displaySecondaryButton && (
          <GridItem className="ln-u-hard-left" size={{ default: '1/1' }}>
            <OutlinedButton
              id={`${buttonTextForIds}-text-btn`}
              className="ln-u-margin-top*2"
              fullWidth
              onClick={onSecondaryClick}
            >
              {secondaryButtonText}
            </OutlinedButton>
          </GridItem>
        )}
      </Card>
    </GridWrapper>
  )
}

SearchCard.propTypes = {
  title: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  displayNames: PropTypes.objectOf(PropTypes.string).isRequired,
  counts: PropTypes.objectOf(PropTypes.number).isRequired,
  gridDefaultSize: PropTypes.string.isRequired,
  displaySecondaryButton: PropTypes.bool,
  secondaryButtonText: PropTypes.string,
  onSecondaryClick: PropTypes.func
}

SearchCard.defaultProps = {
  displaySecondaryButton: false,
  secondaryButtonText: '',
  onSecondaryClick: () => {}
}

export default SearchCard
